import { Injectable } from '@angular/core';
import {
	Data$,
	Notification,
	NotificationAction,
	RemoteData,
	appEntities,
	appEntity,
} from '@lycoris-nubila/ngx-lycoris';
import { flatten, isEmpty } from 'lodash-es';
import uniqBy from 'lodash-es/uniqBy';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import {
	catchError,
	map,
	mapTo,
	startWith,
	switchMap,
	tap,
} from 'rxjs/operators';

import { CommandEvent } from 'application/common/model/command-event';
import { NotificationDomain } from 'application/common/model/notification-domain';
import { ContactApplicationPort } from 'contact/port/contact-application.port';
import { ContactRepositoryPort } from 'contact/port/contact-repository.port';
import { ModuleRepositoryPort } from 'module/port/module-repository.port';
import { CreateCoachMeetingCmd } from 'task/command/create-coach-meeting.cmd';
import { CreateJobOfferMeetingCmd } from 'task/command/create-job-offer-meeting.cmd';
import { CreateTodoTaskCmd } from 'task/command/create-todo-task.cmd';
import { MoveTaskCmd } from 'task/command/move-task.cmd';
import { task } from 'task/model/task.fragments';
import { TaskOutput } from 'task/model/task.output';
import { TaskConverterPort } from 'task/port/task-converter.port';
import { FetchTasksQuery } from 'task/query/fetch-tasks.query';
import { ObserveSearchedUserCoachMeetingsQuery } from 'task/query/observe-searched-user-coach-meetings.query';
import { ITask, Task } from 'task/task';
import { UserApplicationPort } from 'user/port/user-application.port';
import { UserRepositoryPort } from 'user/port/user-repository.port';
import { UserConverterService } from 'user/user-converter.service';

import { AppLocator } from '../../app.locator';
import { AppPushApiPort } from '../common/port/app-push-api.port';
import { CreateContactMeetingCmd } from './command/create-contact-meeting.cmd';
import { TaskApiPort } from './port/task-api.port';
import { TaskApplicationPort } from './port/task-application.port';
import { TaskRepositoryPort } from './port/task-repository.port';

@Injectable()
export class TaskApplicationAdapter implements TaskApplicationPort {
	constructor(
		private _taskApiPort: TaskApiPort,
		private _appPushApiPort: AppPushApiPort,
		private _taskRepositoryPort: TaskRepositoryPort,
		private _userRepositoryPort: UserRepositoryPort,
		private _moduleRepositoryPort: ModuleRepositoryPort,
		private _contactRepositoryPort: ContactRepositoryPort,
		private _taskConverterPort: TaskConverterPort,
		private _userConverterService: UserConverterService
	) {
		this._appPushApiPort
			.observeDomainMessage<null>(NotificationDomain.EXTERNAL_EVENT_TASK)
			.subscribe((message) => this._handleMessage(message));
	}

	private get _contactApplicationPort(): ContactApplicationPort {
		return AppLocator.inject<ContactApplicationPort>(ContactApplicationPort);
	}

	private get _userApplicationPort(): UserApplicationPort {
		return AppLocator.inject<UserApplicationPort>(UserApplicationPort);
	}

	private _handleMessage<T>(message: Notification<NotificationDomain, T>) {
		switch (message.action) {
			case NotificationAction.ADD:
			case NotificationAction.UPDATE:
				this._taskApiPort
					.fetchTaskById(message.entityId)
					.then((task) => this._saveTasks([task]));
				break;
			case NotificationAction.DELETE:
				this._taskRepositoryPort.deleteTask(message.entityId);
				break;
		}
	}

	private _commandHandled$ = new BehaviorSubject<
		CommandEvent<TaskApplicationPort>
	>({
		timestamp: Date.now(),
		entityId: null,
		method: null,
	});

	public get commandHandled$(): Observable<CommandEvent<TaskApplicationPort>> {
		return this._commandHandled$;
	}

	public fetchDayTasks(date: string): Observable<string[] | Error> {
		return this._taskApiPort.fetchDayTasks(date).pipe(
			tap((task) => this._saveTasks(task)),
			mapTo(void 0),
			catchError((e) => of(Error(e)))
		);
	}

	public observeDayTasks(date: DateTime): Observable<ITask[]> {
		return this._userApplicationPort
			.observeSessionUserId()
			.pipe(
				switchMap((userId) =>
					this._taskRepositoryPort.observeUserDayTasks(userId, date)
				)
			);
	}

	public createJobOfferMeeting(cmd: CreateJobOfferMeetingCmd): Observable<void> {
		return this._taskApiPort.createJobOfferMeeting(cmd).pipe(
			tap((task) => this._saveTasks([task])),
			mapTo(null)
		);
	}

	public createContactMeeting(cmd: CreateContactMeetingCmd): Observable<void> {
		return this._taskApiPort.createContactMeeting(cmd).pipe(
			tap((task) => this._saveTasks([task])),
			mapTo(null)
		);
	}

	public moveTask(cmd: MoveTaskCmd): Observable<ITask> {
		return this._taskApiPort.moveTask(cmd).pipe(
			tap((task) => this._saveTasks([task])),
			mapTo(null)
		);
	}

	public createCoachMeeting(cmd: CreateCoachMeetingCmd): Observable<void> {
		return this._taskApiPort.createCoachMeeting(cmd).pipe(
			tap((task) => this._saveTasks([task])),
			tap((task) =>
				this._commandHandled$.next({
					method: 'createCoachMeeting',
					timestamp: Date.now(),
					entityId: null,
				})
			),
			mapTo(null)
		);
	}

	public createTodoTask(cmd: CreateTodoTaskCmd): Observable<string> {
		return this._taskApiPort.createTodoTask(cmd).pipe(
			tap((task) => {
				this._saveTasks([task]);
			}),
			map((task) => task.id)
		);
	}

	public deleteTask(id: string): Promise<void> {
		return from(this._taskApiPort.deleteTask(id))
			.pipe(
				tap(() => this._taskRepositoryPort.deleteTask(id)),
				mapTo(null)
			)
			.toPromise();
	}

	public markTaskDone(id: string): Promise<void> {
		return from(this._taskApiPort.markTaskDone(id))
			.pipe(
				tap((task) => this._saveTasks([task])),
				mapTo(null)
			)
			.toPromise();
	}

	public markTaskPending(id: string): Promise<void> {
		return from(this._taskApiPort.markTaskPending(id))
			.pipe(
				tap((task) => this._saveTasks([task])),
				mapTo(null)
			)
			.toPromise();
	}

	public fetchTasks(query: FetchTasksQuery): Observable<string[] | Error> {
		return this._taskApiPort.fetchTasks(query).pipe(
			tap((tasks) => this._saveTasks(tasks)),
			map((tasks) => tasks.map((task) => task.id)),
			catchError((e) => of(Error(e)))
		);
	}

	public observeTasks(tasksId: string[]): Observable<ITask[]> {
		return this._taskRepositoryPort.observeTasks(tasksId);
	}

	public fetchUserOverdueTasks(userId: string): Observable<string[] | Error> {
		return this._taskApiPort.fetchUserPendingTasks(userId).pipe(
			tap((tasks) => this._saveTasks(tasks)),
			map((tasks) => tasks.map((task) => task.id)),
			catchError((e) => of(Error(e)))
		);
	}

	public observeUserOverdueTasks(userId: string): Observable<ITask[]> {
		return this._taskRepositoryPort.observeUserOverdueTasks(userId);
	}

	public observeUserPendingTasks(
		userId: string
	): Observable<RemoteData<ITask[]>> {
		const api$ = this._taskApiPort.fetchUserPendingTasks(userId).pipe(
			tap((tasks) => this._saveTasks(tasks)),
			map(
				(tasks) =>
					({
						data: tasks,
						fresh: true,
					} as RemoteData<TaskOutput[]>)
			),
			catchError((_) => of({ fresh: true, data: [] } as RemoteData<TaskOutput[]>)),
			startWith({ fresh: false, data: [] } as RemoteData<TaskOutput[]>)
		);

		const repo$ = this._taskRepositoryPort.observeUserPendingTasks(userId);

		return appEntities(api$, repo$);
	}

	public observeJobOfferTasks(jobOfferId: string): Data$<ITask[]> {
		const api$ = this._taskApiPort.fetchJobOfferTasks(jobOfferId).pipe(
			tap((tasks) => this._saveTasks(tasks)),
			map(
				(tasks) =>
					({
						data: tasks,
						fresh: true,
					} as RemoteData<TaskOutput[]>)
			),
			catchError((_) => of({ fresh: true, data: [] } as RemoteData<TaskOutput[]>)),
			startWith({ fresh: false, data: [] } as RemoteData<TaskOutput[]>)
		);

		const repo$ = this._taskRepositoryPort.observeJobOfferTasks(jobOfferId);

		return appEntities(api$, repo$);
	}

	public observeContactTasks(
		contactId: string
	): Observable<RemoteData<ITask[]>> {
		const api$ = this._taskApiPort.fetchContactTasks(contactId).pipe(
			tap((tasks) => this._saveTasks(tasks)),
			map(
				(tasks) =>
					({
						data: tasks,
						fresh: true,
					} as RemoteData<TaskOutput[]>)
			),
			catchError((_) => of({ fresh: true, data: [] } as RemoteData<TaskOutput[]>)),
			startWith({ fresh: false, data: [] } as RemoteData<TaskOutput[]>)
		);

		const repo$ = this._taskRepositoryPort.observeContactTasks(contactId);

		return appEntities(api$, repo$);
	}

	public observeTask(taskId: string): Observable<RemoteData<ITask>> {
		const api$ = from(this._taskApiPort.fetchTaskById(taskId)).pipe(
			tap((task) => this._saveTasks([task])),
			map(
				(task) =>
					({
						data: task,
						fresh: true,
					} as RemoteData<TaskOutput>)
			),
			catchError((_) => of({ fresh: true, data: null } as RemoteData<TaskOutput>)),
			startWith({ fresh: false, data: null } as RemoteData<TaskOutput>)
		);

		const repo$ = this._taskRepositoryPort.observeTask(taskId);

		return appEntity(api$, repo$);
	}

	public saveTasks(ids: string[]): Promise<void> {
		return from(this._taskApiPort.fetchTasksByIds(ids))
			.pipe(
				map((tasks) => this._taskConverterPort.tasksAsEntities(tasks)),
				switchMap((tasks) => this._taskRepositoryPort.saveTasks(tasks))
			)
			.toPromise();
	}

	public observeUserCoachMeetings(meetingsId: string[]): Observable<Task[]> {
		return this._taskRepositoryPort.observeTasks(meetingsId);
	}

	public searchUserCoachMeetings(
		query: ObserveSearchedUserCoachMeetingsQuery
	): Observable<string[] | Error> {
		return this._taskApiPort.searchUserCoachMeetings(query).pipe(
			tap((tasks) => this._saveTasks(tasks)),
			map((tasks) => tasks.map((task) => task.id)),
			catchError((e) => of(Error(e)))
		);
	}

	private _saveTasks(tasks: TaskOutput[]): Promise<void> {
		const users = flatten(
			uniqBy(
				tasks.filter((task) => !isEmpty(task.participants)),
				'id'
			).map((task) =>
				task.participants.map((participant) =>
					this._userConverterService.userAsEntity(participant)
				)
			)
		);

		this._contactApplicationPort.saveContacts(tasks.map((task) => task.contact));

		const entities = uniqBy(tasks, 'id').map((task) =>
			this._taskConverterPort.taskAsEntity(task)
		);

		return Promise.all([
			this._taskRepositoryPort.saveTasks(entities),
			this._userRepositoryPort.saveUsers(users),
		]).then((_) => null);
	}
}
